import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { textoLivre } from '../../nucleo/formato';

/**
 * Trajetórias (19/10, item 2): caminho interno do próprio MalgoOS (começa com uma barra, sem `//` nem `\\`, sem esquema),
 * o único destino de link que a descrição de um card criado pelo SISTEMA (a tarefa do desligamento) pode ter.
 */
export const caminhoInterno = (v: string) => /^\/(?![/\\])[A-Za-z0-9/_?=&.%#-]*$/.test(v) && !/[\\]|\/\//.test(v);

type Bloco = { tipo: 'titulo'; nivel: number; texto: string } | { tipo: 'lista'; ordenada: boolean; itens: string[] } | { tipo: 'codigo'; texto: string } | { tipo: 'paragrafo'; texto: string };

/** Quebra o Markdown em blocos: título (#, ##, ###), lista (-, *, 1.), código (```) e parágrafo. */
export function blocosMarkdown(fonte: string): Bloco[] {
  const linhas = fonte.replace(/\r\n?/g, '\n').split('\n');
  const blocos: Bloco[] = [];
  let i = 0;
  while (i < linhas.length) {
    const l = linhas[i]!;
    if (/^\s*```/.test(l)) {
      const corpo: string[] = [];
      i++;
      while (i < linhas.length && !/^\s*```/.test(linhas[i]!)) corpo.push(linhas[i++]!);
      i++;
      blocos.push({ tipo: 'codigo', texto: corpo.join('\n') });
      continue;
    }
    const titulo = /^(#{1,6})\s+(.*)$/.exec(l);
    if (titulo) {
      blocos.push({ tipo: 'titulo', nivel: Math.min(titulo[1]!.length, 3), texto: titulo[2]!.trim() });
      i++;
      continue;
    }
    const item = /^\s*([-*]|\d+[.)])\s+(.*)$/.exec(l);
    if (item) {
      const ordenada = /\d/.test(item[1]!);
      const itens: string[] = [];
      while (i < linhas.length) {
        const m = /^\s*([-*]|\d+[.)])\s+(.*)$/.exec(linhas[i]!);
        if (!m || /\d/.test(m[1]!) !== ordenada) break;
        itens.push(m[2]!);
        i++;
      }
      blocos.push({ tipo: 'lista', ordenada, itens });
      continue;
    }
    if (!l.trim()) {
      i++;
      continue;
    }
    const corpo: string[] = [];
    while (i < linhas.length && linhas[i]!.trim() && !/^(#{1,6})\s|^\s*([-*]|\d+[.)])\s|^\s*```/.test(linhas[i]!)) corpo.push(linhas[i++]!.trim());
    blocos.push({ tipo: 'paragrafo', texto: corpo.join(' ') });
  }
  return blocos;
}

/**
 * Negrito (**x**) e código (`x`). Todo pedaço passa por `textoLivre` (modo discreto). Link fica como texto: nada vira âncora,
 * salvo com `links` (card criado pelo sistema), em que `[texto](/caminho)` com caminho interno vira link da própria aplicação.
 */
function inline(texto: string, nomes: (string | null | undefined)[], links = false): ReactNode[] {
  const partes = texto.split(links ? /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(\/[^)\s]*\))/g : /(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return partes.map((p, k) => {
    const link = links ? /^\[([^\]]+)\]\((\/[^)\s]*)\)$/.exec(p) : null;
    if (link && caminhoInterno(link[2]!)) {
      return (
        <Link key={k} to={link[2]!} className="font-medium underline hover:text-acento-forte">
          {textoLivre(link[1]!, ...nomes)}
        </Link>
      );
    }
    if (p.startsWith('**') && p.endsWith('**') && p.length > 4) return <strong key={k}>{textoLivre(p.slice(2, -2), ...nomes)}</strong>;
    if (p.startsWith('`') && p.endsWith('`') && p.length > 2) return <code key={k} className="rounded bg-sup-3 px-1 font-mono text-xs">{textoLivre(p.slice(1, -1), ...nomes)}</code>;
    return <span key={k}>{textoLivre(p, ...nomes)}</span>;
  });
}

/**
 * Descrição do card em Markdown, renderizada sem HTML cru (só elementos do React, nunca marcação injetada): o texto do card é dado de
 * terceiros (agente, integração) e nunca vira marcação, script nem link clicável.
 */
export function Markdown({ fonte, nomes = [], links = false }: { fonte: string; nomes?: (string | null | undefined)[]; links?: boolean }) {
  return (
    <div className="flex flex-col gap-2 text-sm leading-relaxed text-tinta-1 break-words">
      {blocosMarkdown(fonte).map((b, i) => {
        if (b.tipo === 'titulo') {
          const classe = b.nivel === 1 ? 'text-base font-semibold' : 'text-sm font-semibold';
          return (
            <p key={i} role="heading" aria-level={b.nivel + 2} className={classe}>
              {inline(b.texto, nomes, links)}
            </p>
          );
        }
        if (b.tipo === 'lista') {
          const Tag = b.ordenada ? 'ol' : 'ul';
          return (
            <Tag key={i} className={`flex flex-col gap-1 pl-5 ${b.ordenada ? 'list-decimal' : 'list-disc'}`}>
              {b.itens.map((it, k) => (
                <li key={k}>{inline(it, nomes, links)}</li>
              ))}
            </Tag>
          );
        }
        if (b.tipo === 'codigo') {
          return (
            <pre key={i} className="overflow-x-auto rounded-lg border border-borda bg-sup-2 p-3 font-mono text-xs whitespace-pre-wrap">
              {textoLivre(b.texto, ...nomes)}
            </pre>
          );
        }
        return <p key={i}>{inline(b.texto, nomes, links)}</p>;
      })}
    </div>
  );
}
